import * as React from "react"
import {
  GatsbyIcon,
  HowItWorksIcon,
  UsersIcon,
  UseCasesIcon,
  FrameworkIcon,
  GlobalEdgeNetworkIcon,
  LivePreviewIcon,
  BuildPipelineIcon,
  FunctionsIcon,
  SourcePluginsIcon,
  AgenciesIcon,
  ConciergeIcon,
  DocumentationIcon,
  PluginsIcon,
  StartersIcon,
  ThemesIcon,
  ContributingIcon,
  HallOfFameIcon,
  WebinarsIcon,
  BlogIcon,
  CommunityIcon,
  AboutUsIcon,
  PartnersIcon,
  DesignLabsIcon,
  CareersIcon,
  ContentHubIcon,
} from "./icons"

export interface IDropdownItemLink {
  href: string
  text: string
}

export interface IDropdownMenuItem {
  text: string
  description?: string
  href: string
  icon: JSX.Element
  links?: IDropdownItemLink[]
}

export interface IDropdownMenuItemGroup {
  title: string
  items: IDropdownMenuItem[]
  span?: number
}

export interface IDropdownMenuProps {
  items: IDropdownMenuItem[] | IDropdownMenuItemGroup[]
  rows?: number
  columns?: number
}

const icon = {
  width: 40,
  height: 40,
}

export const whyGatsbyMenu: IDropdownMenuItem[] = [
  {
    text: "Why Gatsby",
    href: "/why-gatsby",
    icon: <GatsbyIcon {...icon} />,
    description: `See why the world’s top brands choose us`,
  },
  {
    text: `How It Works`,
    href: `/how-it-works/`,
    description: `Learn how Gatsby works on the inside`,
    icon: <HowItWorksIcon {...icon} />,
  },
  {
    text: `Who Uses Gatsby`,
    href: `/customers/`,
    description: `See how our customers use Gatsby`,
    icon: <UsersIcon {...icon} />,
  },
  {
    text: `Use Cases`,
    href: `/use-cases/`,
    description: `Explore how we can help you`,
    icon: <UseCasesIcon {...icon} />,
    links: [
      {
        text: `Developers`,
        href: `/use-cases/developer/`,
      },
      {
        text: `WordPress`,
        href: `/solutions/wordpress/`,
      },
      {
        text: `Software & SaaS`,
        href: `/use-cases/software-saas/`,
      },
      {
        text: `Marketers`,
        href: `/use-cases/marketer/`,
      },
      {
        text: `Shopify`,
        href: `/solutions/shopify/`,
      },
      {
        text: `E-commerce`,
        href: `/use-cases/e-commerce/`,
      },
      {
        text: `Content Creators`,
        href: `/use-cases/content-creator/`,
      },
      {
        text: `Contentful`,
        href: `/solutions/contentful/`,
      },
      {
        text: `Content & Media`,
        href: `/use-cases/content-media/`,
      },
      {
        text: `Finance & Insurance`,
        href: `/use-cases/finance-insurance/`,
      },
      {
        text: `Drupal`,
        href: `/solutions/drupal/`,
      },
      {
        text: `Engineering Leaders`,
        href: `/use-cases/engineering-leader/`,
      },
    ],
  },
]

export const productsMenu: IDropdownMenuItemGroup[] = [
  {
    title: `Deliver Content`,
    span: 2,
    items: [
      {
        text: `Framework`,
        href: `/why-gatsby`,
        description: `Best-in-class speed and performance`,
        icon: <FrameworkIcon {...icon} />,
      },
      {
        text: `Global Edge Network`,
        href: `/products/cloud/hosting`,
        description: `Deliver content at scale, globally`,
        icon: <GlobalEdgeNetworkIcon {...icon} />,
      },
      {
        text: `Live Preview`,
        href: `/products/cloud/previews`,
        description: `Collaborate before publishing`,
        icon: <LivePreviewIcon {...icon} />,
      },
      {
        text: `Build Pipeline`,
        href: `/products/cloud/builds`,
        description: `Publish at the speed of static`,
        icon: <BuildPipelineIcon {...icon} />,
      },
      {
        text: `Functions`,
        href: `/products/cloud/functions`,
        description: `Dynamic experiences without the server`,
        icon: <FunctionsIcon {...icon} />,
      },
    ],
  },
  {
    title: `Orchestrate Content`,
    items: [
      {
        text: `Source Plugins`,
        href: `/plugins/#cms`,
        description: `Integrate your CMS in hours, not days`,
        icon: <SourcePluginsIcon {...icon} />,
      },
      {
        text: `Valhalla Content Hub`,
        href: `/products/valhalla-content-hub`,
        description: `Unified GraphQL API`,
        icon: <ContentHubIcon {...icon} />,
      },
    ],
  },
  {
    title: `Hire an Expert`,
    items: [
      {
        text: `Agencies`,
        href: `https://www.netlify.com/agency-directory/`,
        description: `Find a trusted partner`,
        icon: <AgenciesIcon {...icon} />,
      },
      {
        text: `Concierge`,
        href: `/products/concierge`,
        description: `Tech lead as a service`,
        icon: <ConciergeIcon {...icon} />,
      },
    ],
  },
]

export const openSourceMenu: IDropdownMenuItem[] = [
  {
    text: `Documentation`,
    href: "/docs",
    description: `Start building with the Gatsby ecosystem`,
    icon: <DocumentationIcon {...icon} />,
    links: [
      {
        text: `How-To Guides`,
        href: `/docs/how-to/`,
      },
      {
        text: `Reference Guides`,
        href: `/docs/reference/`,
      },
      {
        text: `Concepts`,
        href: `/docs/conceptual/`,
      },
      {
        text: `Quick Start`,
        href: `/docs/quick-start/`,
      },
      {
        text: `Tutorials`,
        href: `/docs/tutorial/`,
      },
    ],
  },
  {
    text: `Gatsby Plugins`,
    href: "/plugins/",
    icon: <PluginsIcon {...icon} />,
    description: `Customize and add functionality with industry-leading tools`,
    links: [
      {
        text: `WordPress`,
        href: `/plugins/gatsby-source-wordpress/`,
      },
      {
        text: `Contentful`,
        href: `/plugins/gatsby-source-contentful/`,
      },
      {
        text: `Shopify`,
        href: `/plugins/gatsby-source-shopify/`,
      },
      {
        text: `Algolia`,
        href: `/plugins/gatsby-plugin-algolia/`,
      },
      {
        text: `Google Analytics`,
        href: `/plugins/gatsby-plugin-google-analytics/`,
      },
      {
        text: `Segment`,
        href: `/plugins/gatsby-plugin-segment-js/`,
      },
    ],
  },
  {
    text: `Starters`,
    href: `/starters/`,
    icon: <StartersIcon {...icon} />,
    description: `Get up and running with free, optimized templates`,
  },
  {
    text: `Themes`,
    href: `/docs/themes/`,
    icon: <ThemesIcon {...icon} />,
    description: `Gain centralized control over web projects`,
  },
  {
    text: `Contributing`,
    href: `/contributing`,
    icon: <ContributingIcon {...icon} />,
    description: `Leave your legacy on the Gatsby project`,
  },
]

export const learnMenu: IDropdownMenuItem[] = [
  {
    text: `Gatsby Hall of Fame`,
    href: `/showcase/`,
    icon: <HallOfFameIcon {...icon} />,
    description: `Check out what the community is building`,
  },
  {
    text: `Webinars`,
    href: `/resources/webinars/`,
    description: `Learn more with live or on-demand webinars`,
    icon: <WebinarsIcon {...icon} />,
  },
  {
    text: `Blog`,
    href: `/blog/`,
    description: `Read the latest musings from Gatsby`,
    icon: <BlogIcon {...icon} />,
  },
]

export const companyMenu: IDropdownMenuItem[] = [
  {
    text: `About Us`,
    href: `/about/`,
    description: `Meet the team and learn about our story`,
    icon: <AboutUsIcon {...icon} />,
  },
  {
    text: `Partners`,
    href: `/partners/`,
    description: `Join our trusted partner community to unlock exclusive benefits`,
    icon: <PartnersIcon {...icon} />,
    links: [
      {
        text: `Agency Directory`,
        href: `https://www.netlify.com/agency-directory/`,
      },
      {
        text: `Agency Partners`,
        href: `https://www.netlify.com/partners/agency/#hubspot-form-id-contact-our-partner-team`,
      },
      {
        text: `Tech Partners`,
        href: `/partners/technology/`,
      },
    ],
  },
  {
    text: `Design Labs`,
    href: `/design-labs/`,
    description: `Help shape the future of Gatsby`,
    icon: <DesignLabsIcon {...icon} />,
  },
  {
    text: `Careers`,
    href: `/careers/`,
    description: `Come work with us!`,
    icon: <CareersIcon {...icon} />,
  },
]
